import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import {
  FC, ReactNode, useCallback, useMemo,
} from 'react';
import { useFormState } from 'react-final-form';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import Typography from 'src/common/components/Typography';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { formatDateRange } from 'src/common/utils/dateHelpers';
import getClassNames from 'src/common/utils/getClassNames';
import { getDurationDigits } from 'src/listing/helper';
import { TripCartFormState } from 'src/listing/types';
import getCaptainOption from 'src/listing/utils/getCaptainOption';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { TRIP_CART_PANEL_PRICE_DETAILS, TRIP_CART_PANEL_ROOT } from 'src/listing/constants';
import styles from './TripCartSummaryHeader.module.scss';

const placeholder = (value: ReactNode) => value || ' ';

const TripCartSummaryHeader: FC = () => {
  const [t] = useTranslation('listing-page');
  const [tCommon] = useTranslation('common');
  const { showPanel, isOpen } = useTripCartContext();
  const {
    values:
    {
      formSelectedDates,
      formDuration,
      formGuests,
      withCaptain,
      isMultiDay,
    },
  } = useFormState<TripCartFormState>();
  const {
    priceLoading, priceUnit, prices, getFormattedEstimatedRate, options: { isCostConfirmed },
  } = useTripCartOptionsContext();
  const { isDateUnavailable } = useBookingMotivatorsContext();
  const dateLocale = useDateFnsLocale();
  const hourlyDuration = getDurationDigits(formDuration);

  // Will never show 'optional'
  const captainedType = getCaptainOption([withCaptain]);

  const dateRange = formatDateRange(
    formSelectedDates[0],
    formSelectedDates[1],
    dateLocale,
  );

  const unavailableDate = isDateUnavailable(formSelectedDates, isMultiDay);
  const { cost, costText } = useMemo(() => {
    const showEstimatedRate = prices?.rate != null;
    const formattedRate = showEstimatedRate ? getFormattedEstimatedRate(prices?.rate || '') : '';
    const estimatedRateText = showEstimatedRate ? t('trip-cart--estimated-rate--text') : t('trip-cart--estimated-cost');

    const estimatedCost = prices?.price_sections?.[0]?.formatted_amount || '';

    return {
      cost: showEstimatedRate ? formattedRate : estimatedCost,
      costText: isCostConfirmed ? t('trip-cart--confirmed-cost') : estimatedRateText,
    };
  }, [
    getFormattedEstimatedRate,
    prices?.price_sections,
    prices?.rate,
    isCostConfirmed,
    t,
  ]);

  const isLoading = !formSelectedDates.length || priceLoading;

  const handlePriceDetailsClick = useCallback(() => {
    if (isOpen) {
      return;
    }

    showPanel(TRIP_CART_PANEL_PRICE_DETAILS);
  }, [isOpen, showPanel]);

  const handleTripDetailsClick = useCallback(() => {
    if (isOpen) {
      return;
    }

    showPanel(TRIP_CART_PANEL_ROOT);
  }, [isOpen, showPanel]);

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.content}
        onPointerDown={handleTripDetailsClick}
        aria-label={tCommon('bottom-drawer--open')}
      >
        <SkeletonBlock loading={isLoading}>
          <Typography
            type="span"
            size="body-l"
            weight="semibold"
            className={getClassNames(styles, 'date', classNames({ unavailableDate }))}
          >
            {placeholder(dateRange)}
          </Typography>
        </SkeletonBlock>
        <SkeletonBlock loading={isLoading}>
          <div className={styles.summary}>
            <Typography
              type="span"
              size="body-s"
              sizeFromBreakpointL="body-m"
            >
              {t(`trip-cart--duration-${priceUnit}`, {
                duration: hourlyDuration,
                count: hourlyDuration,
              })}
            </Typography>
            <div className={styles.summarySplit} />
            <Typography
              type="span"
              size="body-s"
              sizeFromBreakpointL="body-m"
            >
              {formGuests}
              {' '}
              {t('trip-cart--guests')}
            </Typography>
          </div>
        </SkeletonBlock>
        <SkeletonBlock loading={isLoading}>
          <div className={styles.summary}>
            <Typography
              type="span"
              size="body-s"
              sizeFromBreakpointL="body-m"
              data-test="captain-type"
            >
              {placeholder(t(`captain-option--${captainedType}`))}
            </Typography>
          </div>
        </SkeletonBlock>
      </button>
      <button
        type="button"
        className={getClassNames(styles, 'content', 'price-details')}
        onClick={handlePriceDetailsClick}
        aria-label={tCommon('bottom-drawer--open')}
      >
        <SkeletonBlock loading={isLoading}>
          <Typography
            type="span"
            size="body-l"
            weight="semibold"
          >
            {placeholder(cost)}
          </Typography>
        </SkeletonBlock>
        <SkeletonBlock loading={isLoading}>
          <Typography
            type="span"
            size="body-s"
            sizeFromBreakpointL="body-m"
            className={styles.costText}
          >
            {placeholder(costText)}
          </Typography>
        </SkeletonBlock>
      </button>
    </div>
  );
};

export default TripCartSummaryHeader;
